import React, { useState } from 'react';
import { Button, Dropdown, Progress, Segmented, Tag, Tooltip, Typography } from 'antd';
import {
    GlobalOutlined, CloudServerOutlined, SafetyCertificateOutlined, MailOutlined, DesktopOutlined,
    AppstoreOutlined, HddOutlined, DatabaseOutlined, RobotOutlined, CloseCircleFilled, WarningFilled,
    InfoCircleFilled, CheckCircleFilled, RightOutlined, PlusOutlined, WalletOutlined, FileTextOutlined,
    ShoppingCartOutlined, ClusterOutlined, ApartmentOutlined, CodeSandboxOutlined, SendOutlined, LoadingOutlined, CloudOutlined,
    DownOutlined,
} from '@ant-design/icons';
import { ONB_PRODUCTS, onbProduct } from './onboardingProducts';
import { Link } from 'react-router-dom';
import { userPath } from '../../../shared/config/paths';
import Money from '../../../shared/components/Money';
import BalanceMoney from '../../../shared/components/BalanceMoney';
import StatusTag from '../../../shared/components/StatusTag';
import { ContainerStatusTag, specText } from '../../../shared/components/container/ContainerParts';
import { formatDate, formatDateTime } from '../../../shared/utils/date';
import { formatBytes } from '../../../shared/utils/format';
import { MiniTrend, RegionMap } from './charts';
import CountryFlag from '../../../shared/components/CountryFlag';
import i18n, { t, useTranslation } from '../../../shared/i18n';

const { Text } = Typography;

// ---------------------------------------------------------------- formatters
// 按当前语言格式化：中文 1.2万/3.4亿，其它语言用 Intl 紧凑写法(27K / 2.7万 / 2.7만 / 27 тыс.)
const uiLang = () => (i18n.language || 'zh-CN');
export const fmtNum = (n) => {
    const v = Number(n || 0);
    const lang = uiLang();
    if (v < 1e4) return new Intl.NumberFormat(lang).format(v);
    try {
        return new Intl.NumberFormat(lang, { notation: 'compact', maximumFractionDigits: v >= 1e6 ? 0 : 1 }).format(v);
    } catch (_) {
        return new Intl.NumberFormat(lang).format(v);
    }
};
// 地区名：有 ISO 代码时按当前语言显示(法国 → France / フランス)，否则用后端名称
const regionName = (r) => {
    if (r.code) {
        try {
            const name = new Intl.DisplayNames([uiLang()], { type: 'region' }).of(r.code.toUpperCase());
            if (name) return name;
        } catch (_) { /* ignore */ }
    }
    return r.name;
};
const fmtBps = (bps) => {
    const v = Number(bps || 0);
    if (v >= 1e9) return `${(v / 1e9).toFixed(2)} Gbps`;
    if (v >= 1e6) return `${(v / 1e6).toFixed(2)} Mbps`;
    if (v >= 1e3) return `${(v / 1e3).toFixed(1)} Kbps`;
    return `${Math.round(v)} bps`;
};
const fmtMb = (mb) => formatBytes(Number(mb || 0) * 1024 * 1024);
const fmtPct = (r) => `${(Number(r || 0) * 100).toFixed(1)}%`;
const pctOf = (used, total) => (total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0);
const daysText = (d) => (d === null || d === undefined ? '' : d < 0 ? t('Expired') : d === 0 ? t('Today') : t('{{d}} days', { d }));
const arr = (v) => (Array.isArray(v) ? v : v && typeof v === 'object' ? Object.values(v) : []);

const L = ({ to, children, ...rest }) => <Link to={userPath(to)} {...rest}>{children}</Link>;

// ---------------------------------------------------------------- shared bits
export const Kpi = ({ label, value, sub, tone, tip }) => (
    <div className="dw-kpi">
        <div className="dw-kpi-label">{tip ? <Tooltip title={tip}>{label}</Tooltip> : label}</div>
        <div className={`dw-kpi-value${tone ? ` is-${tone}` : ''}`}>{value}</div>
        {sub ? <div className="dw-kpi-sub">{sub}</div> : null}
    </div>
);

const Row = ({ left, right, to }) => {
    const inner = (
        <>
            <span className="dw-row-l">{left}</span>
            <span className="dw-row-r">{right}</span>
        </>
    );
    return to ? <L to={to} className="dw-row dw-row-link">{inner}</L> : <div className="dw-row">{inner}</div>;
};

const NoData = ({ text }) => {
    const { t } = useTranslation();
    return <div className="dw-nodata">{text === undefined ? t('No data yet') : text}</div>;
};

/** Thin usage bar row: name · used / total · bar */
const UsageRow = ({ name, to, used, total, label }) => {
    const pct = pctOf(used, total);
    return (
        <L to={to} className="dw-use">
            <span className="dw-use-head">
                <span className="dw-row-l">{name}</span>
                <span className="dw-row-r">{label}</span>
            </span>
            <Progress percent={total > 0 ? pct : 0} showInfo={false} size="small" status={pct >= 100 ? 'exception' : 'normal'} strokeColor={pct >= 80 && pct < 100 ? '#d48806' : undefined} />
        </L>
    );
};

const Days = ({ d }) => <span className={d !== null && d !== undefined && d <= 30 ? 'dw-warn-text' : ''}>{daysText(d)}</span>;

const Provisioning = ({ items, render }) => {
    const { t } = useTranslation();
    if (!items || !items.length) return null;
    return items.map((p, i) => (
        <Row key={`p${p.subscription_id || p.id || i}`} left={<><LoadingOutlined className="dw-spin" /> {render(p)}</>} right={<Tag color="processing" style={{ marginInlineEnd: 0 }}>{t('Activating')}</Tag>} />
    ));
};

// ---------------------------------------------------------------- product lines
export const PRODUCTS = {
    cdn: { name: 'CDN', icon: <CloudServerOutlined />, route: '/cdn/domains', buy: '/cdn/domains/new' },
    dns: { name: 'DNS', icon: <ApartmentOutlined />, route: '/dns/zones', buy: '/dns/zones' },
    evm: { name: '云服务器', icon: <DesktopOutlined />, route: '/evm/vms', buy: '/evm/vms/purchase' },
    container: { name: '独享容器', icon: <CodeSandboxOutlined />, route: '/containers', buy: '/containers/create' },
    host: { name: '独享容器（旧版）', icon: <HddOutlined />, route: '/containers', buy: null },
    host_shared: { name: '共享主机', icon: <DatabaseOutlined />, route: '/host_shared/vhosts', buy: '/provision/host_shared' },
    vpc: { name: '网络', icon: <ClusterOutlined />, route: '/evm/vpc', buy: '/evm/vpc' },
    ssl: { name: 'SSL 证书', icon: <SafetyCertificateOutlined />, route: '/ssl/certificates', buy: '/ssl/browse' },
    domain: { name: '域名', icon: <GlobalOutlined />, route: '/domain-registration/my-domains', buy: '/domain-registration/search' },
    mail: { name: '邮箱', icon: <MailOutlined />, route: '/webmail/domains', buy: '/webmail/domains' },
    tm: { name: '邮件推送', icon: <SendOutlined />, route: '/tm/streams', buy: '/tm/streams' },
    disk: { name: '云盘', icon: <CloudOutlined />, route: '/disk', buy: '/disk' },
    backup: { name: '备份', icon: <HddOutlined />, route: '/evm/vms', buy: null },
    llm: { name: 'LLM API', icon: <RobotOutlined />, route: '/llm/account', buy: '/llm/subscription' },
};

/** One-line "开通" CTAs for product lines the user doesn't use. */
export const CtaLine = ({ keys }) => {
    const { t } = useTranslation();
    const items = keys.map((k) => ({ k, ...PRODUCTS[k] })).filter((p) => p.buy);
    if (!items.length) return null;
    return (
        <div className="dw-cta">
            <Text type="secondary">{t('Not activated')}</Text>
            {items.map((p) => (
                <L key={p.k} to={p.buy} className="dw-cta-chip"><PlusOutlined /> {t(p.name)}</L>
            ))}
        </div>
    );
};

/** In-card empty state for a product card the user has none of (any more). */
export const EmptyProduct = ({ product }) => {
    const { t } = useTranslation();
    const p = PRODUCTS[product];
    return (
        <div className="dw-empty">
            <span className="dw-empty-icon">{p?.icon}</span>
            <Text type="secondary">{t('None yet')}</Text>
            {p?.buy ? <L to={p.buy}><Button size="small" icon={<PlusOutlined />}>{t('Enable')}</Button></L> : null}
        </div>
    );
};

// ---------------------------------------------------------------- widgets

const ATT = {
    critical: { icon: <CloseCircleFilled />, label: '紧急' },
    warning: { icon: <WarningFilled />, label: '注意' },
    info: { icon: <InfoCircleFilled />, label: '提示' },
};

const Attention = ({ data }) => {
    const { t } = useTranslation();
    const [all, setAll] = useState(false);
    const items = data?.items || [];
    if (!items.length) {
        return <div className="dw-allgood"><CheckCircleFilled /> {t('Everything looks good. Nothing needs your attention.')}</div>;
    }
    const shown = all ? items : items.slice(0, 7);
    return (
        <div className="dw-att">
            {shown.map((it, i) => {
                const s = ATT[it.severity] || ATT.info;
                return (
                    <L key={`${it.kind}-${i}`} to={it.link} className={`dw-att-item is-${it.severity}`}>
                        <span className="dw-att-icon" aria-label={t(s.label)}>{s.icon}</span>
                        <span className="dw-att-body">
                            <span className="dw-att-title">{it.title_key ? t(it.title_key, it.title_params || {}) : t(it.title)}</span>
                            {it.meta ? <span className="dw-att-meta">{it.meta_key ? t(it.meta_key, it.meta_params || {}) : t(it.meta)}</span> : null}
                        </span>
                        <span className="dw-att-action">{t(it.action)} <RightOutlined /></span>
                    </L>
                );
            })}
            {items.length > 7 ? (
                <Button type="link" size="small" onClick={() => setAll(!all)} style={{ paddingLeft: 0 }}>
                    {all ? t('Collapse') : t('Expand all {{count}} items', { count: items.length })}
                </Button>
            ) : null}
        </div>
    );
};

const INVOICE_TYPE = {
    subscription: '订阅', renewal: '续费', upgrade: '升级', addon: '增值', refund: '退款', adjustment: '调整', ssl: 'SSL',
    domain_registration: '域名注册', domain_renewal: '域名续费', domain_transfer: '域名转入', domain_restore: '域名赎回', topup: '充值',
};

const RenewalRows = ({ rows, cur }) => {
    const { t } = useTranslation();
    return rows.length ? rows.map((r) => (
        <Row
            key={r.id}
            to="/billing/subscription"
            left={<>{r.name}{r.auto_renew ? <Text type="secondary"> {t('· Auto')}</Text> : null}</>}
            right={<><Money amount={r.price} currency={cur} /> · {formatDate(r.due_at)}</>}
        />
    )) : <NoData text={t('No subscriptions due for renewal soon')} />;
};

const InvoiceRows = ({ rows }) => {
    const { t } = useTranslation();
    return rows.length ? rows.map((r) => (
        <Row
            key={r.id}
            to={`/billing/invoices/${r.id}`}
            left={<>{t(INVOICE_TYPE[r.type] || r.type)} <Text type="secondary" className="dw-mono">{formatDate(r.at)}</Text></>}
            right={<><Money amount={r.amount} currency={r.currency} /> <StatusTag status={r.status} /></>}
        />
    )) : <NoData text={t('No invoices yet')} />;
};

const Account = ({ data, view }) => {
    const { t } = useTranslation();
    const cur = data.currency;
    if (view === 'renewals') {
        return (
            <div>
                <div className="dw-kpis dw-kpis-2">
                    <Kpi label={t('Due in 30 days')} value={<Money amount={data.due30 || 0} currency={cur} />} />
                    <Kpi label={t('Subscription')} value={(data.renewals || []).length} />
                </div>
                <div className="dw-sub-title">{t('Renewal due soon')}</div>
                <RenewalRows rows={data.renewals || []} cur={cur} />
            </div>
        );
    }
    if (view === 'invoices') {
        const u = data.unpaid || {};
        return (
            <div>
                <div className="dw-kpis dw-kpis-2">
                    <L to="/billing/invoices" className="dw-kpi-link">
                        <Kpi label={t('Unpaid invoices')} value={u.count || 0} tone={u.overdue ? 'danger' : u.count ? 'warn' : null} sub={u.overdue ? t('{{count}} overdue', { count: u.overdue }) : null} />
                    </L>
                </div>
                <div className="dw-sub-title">{t('Recent invoices')}</div>
                <InvoiceRows rows={data.invoices || []} />
            </div>
        );
    }
    const unpaid = data.unpaid || {};
    const spend = data.spend || [];
    const primarySpend = spend.find((s) => s.currency === cur);
    const otherSpend = spend.filter((s) => s.currency !== cur && s.this_month > 0);
    return (
        <div>
            <div className="dw-balance">
                <div>
                    <div className="dw-kpi-label">{t('Balance')}</div>
                    <div className="dw-balance-value"><BalanceMoney view={data.balance_view || { balance: data.balance, currency: cur }} /></div>
                </div>
                <L to="/billing/balance"><Button type="primary" icon={<WalletOutlined />}>{t('Top up')}</Button></L>
            </div>
            <div className="dw-kpis dw-kpis-2">
                <L to="/billing/invoices" className="dw-kpi-link">
                    <Kpi
                        label={t('Unpaid invoices')}
                        value={unpaid.count ? t('{{count}} invoices', { count: unpaid.count }) : t('None')}
                        tone={unpaid.overdue ? 'danger' : unpaid.count ? 'warn' : null}
                        sub={(unpaid.by_currency || []).map((u) => (
                            <span key={u.currency} className="dw-money-chip"><Money amount={u.amount} currency={u.currency} /></span>
                        ))}
                    />
                </L>
                <Kpi
                    label={t('Spend this month')}
                    value={<Money amount={primarySpend?.this_month || 0} currency={cur} />}
                    sub={(
                        <>
                            {otherSpend.map((s) => <span key={s.currency} className="dw-money-chip">+<Money amount={s.this_month} currency={s.currency} /></span>)}
                            {primarySpend ? <span>{t('Last month')} <Money amount={primarySpend.last_month} currency={cur} /></span> : null}
                        </>
                    )}
                />
            </div>
            <div className="dw-sub-title">{t('Renewal due soon')}</div>
            <RenewalRows rows={data.renewals || []} cur={cur} />
        </div>
    );
};

const Services = ({ data }) => {
    const { t } = useTranslation();
    const lines = data.lines || [];
    const used = lines.filter((l) => !l.error && l.total > 0);
    const unused = lines.filter((l) => !l.error && l.total === 0).map((l) => l.key);
    return (
        <div>
            {used.length ? (
                <div className="dw-svc-grid">
                    {used.map((l) => {
                        const p = PRODUCTS[l.key] || { name: l.key, icon: <AppstoreOutlined />, route: '/billing/subscription' };
                        return (
                            <L key={l.key} to={p.route} className="dw-svc">
                                <span className="dw-svc-icon">{p.icon}</span>
                                <span className="dw-svc-main">
                                    <span className="dw-svc-name">{t(p.name)}</span>
                                    <span className="dw-svc-nums">
                                        <b>{l.total}</b>
                                        <span className="dw-dot is-ok" />{l.active}
                                        {l.suspended ? <><span className="dw-dot is-bad" />{l.suspended}</> : null}
                                        {l.expiring ? <><span className="dw-dot is-warn" />{l.expiring}</> : null}
                                        {l.pending ? <><span className="dw-dot is-info" />{l.pending}</> : null}
                                    </span>
                                </span>
                            </L>
                        );
                    })}
                </div>
            ) : <NoData text={t('No services yet')} />}
            <div className="dw-legend">
                <span><span className="dw-dot is-ok" />{t('Normal')}</span>
                <span><span className="dw-dot is-bad" />{t('Suspended')}</span>
                <span><span className="dw-dot is-warn" />{t('Expires within 30 days')}</span>
                {used.some((l) => l.pending) ? <span><span className="dw-dot is-info" />{t('Activating')}</span> : null}
            </div>
            <CtaLine keys={unused} />
        </div>
    );
};

const TopBars = ({ rows, value, format }) => {
    const max = Math.max(1, ...rows.map(value));
    return (
        <div className="dw-top">
            {rows.map((r) => (
                <div key={r.host || r.zone || r.name} className="dw-top-row">
                    <span className="dw-top-name" title={r.host || r.zone || r.name}>{r.host || r.zone || r.name}</span>
                    <span className="dw-top-bar"><span style={{ width: `${Math.max(2, (value(r) / max) * 100)}%` }} /></span>
                    <span className="dw-top-val">{format(r)}</span>
                </div>
            ))}
        </div>
    );
};

const Cdn = ({ data, ctx, view }) => {
    const { t } = useTranslation();
    const kpis = (
        <div className="dw-kpis dw-kpis-4">
            <Kpi label={t('Traffic')} value={formatBytes(data.bytes)} />
            <Kpi label={t('Request')} value={fmtNum(data.requests)} />
            <Kpi label={t('Cache hit')} value={`${Number(data.hit_rate || 0).toFixed(1)}%`} tip={t('Count requests (HIT/STALE)')} />
            {view === 'traffic' || view === 'requests'
                ? <Kpi label={t('Peak bandwidth')} value={fmtBps(data.peak_bps)} tip={t('Hourly average')} />
                : <Kpi label={t('Avg response')} value={`${data.avg_ms || 0} ms`} />}
        </div>
    );
    const top = data.top || [];
    if (view === 'top' || view === 'hit') {
        const rows = view === 'hit' ? [...top].sort((a, b) => b.requests - a.requests) : top;
        return (
            <div>
                {kpis}
                {rows.length ? (view === 'hit'
                    ? <TopBars rows={rows} value={(r) => r.hit_rate} format={(r) => `${Number(r.hit_rate || 0).toFixed(1)}% · ${fmtNum(r.requests)}`} />
                    : <TopBars rows={rows} value={(r) => r.bytes} format={(r) => formatBytes(r.bytes)} />)
                    : <NoData text={t('No access logs for this period')} />}
            </div>
        );
    }
    const isReq = view === 'requests';
    const series = (data.series || []).map((p) => ({ t: p.t, v: isReq ? p.requests : p.bytes }));
    return (
        <div>
            {kpis}
            {series.length ? (
                <MiniTrend
                    points={series}
                    format={isReq ? (v) => t('{{count}} times', { count: new Intl.NumberFormat(uiLang()).format(v) }) : formatBytes}
                    name={isReq ? t('Request') : t('Traffic')}
                    timeFormat={ctx.cdnRange === '7d' ? 'MM-DD' : 'HH:mm'}
                />
            ) : <NoData text={t('No access logs for this period')} />}
        </div>
    );
};

const NS_TAG = { active: ['已接入', 'success'], pending: ['未接入', 'warning'], error: ['检测失败', 'error'] };

const Dns = ({ data, view }) => {
    const { t } = useTranslation();
    if (view === 'zones') {
        return (
            <div>
                <div className="dw-kpis dw-kpis-3">
                    <Kpi label={t('Zone')} value={data.zones} sub={t('{{count}} enabled', { count: data.active })} />
                    <Kpi label={t('Records')} value={fmtNum(data.records)} />
                </div>
                <div className="dw-sub-title">{t('Zone')}</div>
                {(data.list || []).map((z) => {
                    const ns = NS_TAG[z.ns_status] || NS_TAG.pending;
                    return (
                        <Row
                            key={z.id}
                            to={`/dns/records/${z.id}`}
                            left={z.zone}
                            right={<>{t('{{count}} entries', { count: z.records })} {z.status === 'active' ? <Tag color={ns[1]} style={{ marginInlineEnd: 0 }}>{t(ns[0])}</Tag> : <StatusTag status={z.status} />}</>}
                        />
                    );
                })}
            </div>
        );
    }
    if (view === 'top') {
        return (
            <div>
                <div className="dw-kpis dw-kpis-3">
                    <Kpi label={t('24h queries')} value={fmtNum(data.queries)} />
                    <Kpi label={t('Zone')} value={data.zones} />
                </div>
                {(data.top || []).length
                    ? <TopBars rows={data.top} value={(r) => r.count} format={(r) => fmtNum(r.count)} />
                    : <NoData text={t('No queries')} />}
            </div>
        );
    }
    return (
        <div>
            <div className="dw-kpis dw-kpis-4">
                <Kpi label={t('Zone')} value={data.zones} sub={t('{{count}} enabled', { count: data.active })} />
                <Kpi label={t('Records')} value={fmtNum(data.records)} />
                <Kpi label={t('24h queries')} value={data.queries === null || data.queries === undefined ? '—' : fmtNum(data.queries)} />
                <L to="/dns/zones" className="dw-kpi-link">
                    <Kpi label={t('NS setup incomplete')} value={data.ns_pending} tone={data.ns_pending ? 'warn' : null} sub={t('{{count}} connected', { count: data.ns_ok })} />
                </L>
            </div>
            {data.queries_error ? <NoData text={t('Query logs are temporarily unavailable')} />
                : (data.series || []).length ? <MiniTrend points={data.series.map((p) => ({ t: p.t, v: p.count }))} format={(v) => t('{{count}} times', { count: new Intl.NumberFormat(uiLang()).format(v) })} name={t('Query')} />
                    : <NoData />}
        </div>
    );
};

const EVM_STATES = ['active', 'create', 'rebuild', 'migrate', 'suspend', 'flow_limit', 'error'];

const VmName = ({ v }) => (
    <>
        {v.name}
        {v.kind === 'vds' ? <Tag color="purple" className="dw-mini-tag">VDS</Tag> : null}
    </>
);

const EvmList = ({ data, view }) => {
    const { t } = useTranslation();
    const list = data.list || [];
    const pending = (
        <Provisioning items={data.pending} render={(p) => <>{p.hostname || p.product || t('Cloud servers')}{p.kind === 'vds' ? <Tag color="purple" className="dw-mini-tag">VDS</Tag> : null}</>} />
    );
    if (view === 'usage') {
        return (
            <div>
                {list.length ? list.map((v) => (
                    <div key={v.id} className="dw-vm-use">
                        <UsageRow
                            name={<VmName v={v} />}
                            to={`/evm/vms/${v.id}`}
                            used={v.traffic_used_mb}
                            total={v.traffic_limit_mb}
                            label={v.traffic_limit_mb > 0 ? `${fmtMb(v.traffic_used_mb)} / ${fmtMb(v.traffic_limit_mb)}` : `${fmtMb(v.traffic_used_mb)} · ${t('Unlimited traffic')}`}
                        />
                        {v.cpu !== null && v.cpu !== undefined ? (
                            <div className="dw-vm-metrics">CPU {v.cpu}% · {t('Memory')} {fmtMb(v.mem_used_mb)} / {fmtMb(v.mem_total_mb)}</div>
                        ) : null}
                    </div>
                )) : <NoData />}
                {!data.has_metrics && list.length ? <div className="dw-foot">{t('CPU / memory: no data reported by the node yet')}</div> : null}
            </div>
        );
    }
    return (
        <div>
            {pending}
            {list.length ? list.map((v) => (
                <Row
                    key={v.id}
                    to={`/evm/vms/${v.id}`}
                    left={<><VmName v={v} /> <Text type="secondary" className="dw-mono">{v.ip || ''}</Text></>}
                    right={view === 'expiring'
                        ? <><Days d={v.days_left} />{v.auto_renew ? <Text type="secondary">{t('· Auto')}</Text> : null}</>
                        : <StatusTag status={v.status} domain="evm" />}
                />
            )) : (!data.pending || !data.pending.length) ? <NoData /> : null}
            {data.total > list.length ? <L to="/evm/vms" className="dw-foot dw-foot-link">{t('All {{count}}', { count: data.total })} <RightOutlined /></L> : null}
        </div>
    );
};

const Evm = ({ data, size, view }) => {
    const { t } = useTranslation();
    if (view && view !== 'overview') return <EvmList data={data} view={view} />;
    const m = data.metrics || {};
    const tr = data.traffic || {};
    const pct = tr.limit_mb > 0 ? Math.min(100, Math.round((tr.used_mb / tr.limit_mb) * 100)) : 0;
    const regions = data.regions || [];
    const mine = regions.filter((r) => r.instances > 0);
    const avail = regions.filter((r) => r.available);
    const states = Object.entries(data.by_state || {}).sort((a, b) => EVM_STATES.indexOf(a[0]) - EVM_STATES.indexOf(b[0]));
    const stats = (
        <div className="dw-evm-stats">
            <div className="dw-kpis dw-kpis-2">
                <Kpi label={t('Instances')} value={data.total} sub={states.map(([s, n]) => <span key={s} className="dw-state"><StatusTag status={s} domain="evm" /> {n}</span>)} />
                <Kpi label={t('Configuration total')} value={t('{{vcpu}} cores · {{memory}}', { vcpu: data.vcpu, memory: fmtMb(data.memory_mb) })} />
            </div>
            <div className="dw-sub-title">{t('Traffic this month')}</div>
            {tr.count > 0 ? (
                <>
                    <div className="dw-flow">
                        <span>{fmtMb(tr.used_mb)} / {fmtMb(tr.limit_mb)}</span>
                        <Text type="secondary">{t('Limit: {{count}} instances', { count: tr.count })}</Text>
                    </div>
                    <Progress percent={pct} showInfo={false} size="small" status={pct >= 100 ? 'exception' : 'normal'} strokeColor={pct >= 80 && pct < 100 ? '#d48806' : undefined} />
                    {(tr.top || []).filter((v) => v.pct >= 50).map((v) => (
                        <Row key={v.id} to={`/evm/vms/${v.id}`} left={v.name} right={`${v.pct}%`} />
                    ))}
                </>
            ) : <div className="dw-flow"><span>{fmtMb(tr.unlimited_used_mb)}</span><Text type="secondary">{t('Unlimited traffic')}</Text></div>}
            <div className="dw-sub-title">{t('CPU / Memory')}</div>
            <div className="dw-kpis dw-kpis-2">
                <Kpi label={t('Average CPU')} value={m.has_metrics ? `${m.cpu_avg}%` : '-'} sub={m.has_metrics ? t('Peak {{value}}%', { value: m.cpu_max }) : null} />
                <Kpi label={t('Memory')} value={m.has_metrics ? fmtMb(m.mem_used_mb) : '-'} sub={m.has_metrics ? t('of {{memory}}', { memory: fmtMb(m.mem_total_mb) }) : null} />
            </div>
        </div>
    );
    const map = (
        <div className="dw-evm-map">
            <RegionMap regions={regions} height={size === 'full' ? 240 : 200} />
            <div className="dw-map-legend">
                <span><span className="dw-sw is-mine" />{t('My instances')}</span>
                <span><span className="dw-sw is-avail" />{t('Available for activation')}</span>
            </div>
            <div className="dw-region-chips">
                {(mine.length ? mine : avail).map((r) => (
                    <span key={r.area_id} className={`dw-region${r.instances ? ' is-mine' : ''}`} title={(r.zones || []).join('、')}>
                        <CountryFlag code={r.code} style={{ marginInlineEnd: 4 }} />{regionName(r)}{r.instances ? <b> {r.instances}</b> : null}
                    </span>
                ))}
                {mine.length ? <Text type="secondary" style={{ fontSize: 12 }}>{t('Available in {{count}} regions', { count: avail.length })}</Text> : null}
            </div>
        </div>
    );
    return <div className={`dw-evm${size === 'full' ? ' is-wide' : ''}`}>{stats}{map}</div>;
};

const HostShared = ({ data, view }) => {
    const { t } = useTranslation();
    const list = data.list || [];
    if (view === 'usage') {
        return (
            <div>
                <div className="dw-kpis dw-kpis-2">
                    <Kpi label={t('Disk')} value={`${fmtMb(data.disk_used_mb)}`} sub={t('{{space}} total', { space: fmtMb(data.disk_quota_mb) })} />
                    <Kpi label={t('Traffic this month')} value={`${data.bw_used_gb || 0} GB`} sub={t('{{space}} total', { space: `${data.bw_quota_gb || 0} GB` })} />
                </div>
                <div className="dw-sub-title">{t('Disk usage')}</div>
                {list.map((v) => (
                    <UsageRow key={v.id} name={v.domain || v.name} to={`/host_shared/vhosts/${v.id}`} used={v.disk_used_mb} total={v.disk_quota_mb}
                        label={`${fmtMb(v.disk_used_mb)} / ${fmtMb(v.disk_quota_mb)}`} />
                ))}
            </div>
        );
    }
    return (
        <div>
            {view !== 'expiring' ? <Provisioning items={data.pending} render={(p) => p.domain || p.product || t('Shared hosting')} /> : null}
            {list.length ? list.map((v) => (
                <Row
                    key={v.id}
                    to={`/host_shared/vhosts/${v.id}`}
                    left={<>{v.domain || v.name}{v.domains > 1 ? <Text type="secondary"> +{v.domains - 1}</Text> : null}</>}
                    right={view === 'expiring'
                        ? <><Days d={v.days_left} />{v.auto_renew ? <Text type="secondary">{t('· Auto')}</Text> : null}</>
                        : <StatusTag status={v.status} />}
                />
            )) : (view === 'expiring' || !(data.pending || []).length) ? <NoData /> : null}
        </div>
    );
};

const Container = ({ data, view }) => {
    const { t } = useTranslation();
    const pending = data.pending || [];
    const list = data.list || [];
    if (view === 'pending') {
        return pending.length ? (
            <Provisioning items={pending} render={(p) => <>{p.label || p.image || t('Dedicated Containers')} <Text type="secondary">{specText(p)}</Text></>} />
        ) : <NoData text={t('No orders being provisioned')} />;
    }
    if (view === 'usage') {
        return list.length ? list.map((c) => {
            const u = c.usage || {};
            const memLimit = u.mem_limit_bytes || (c.memory_mb || 0) * 1048576;
            const diskLimit = (c.disk_gb || 0) * 1024 ** 3;
            return (
                <div key={c.id} className="dw-vm-use">
                    <UsageRow name={c.name} to={`/containers/${c.id}`} used={u.mem_used_bytes || 0} total={memLimit}
                        label={`${t('Memory')} ${formatBytes(u.mem_used_bytes || 0)} / ${formatBytes(memLimit)}`} />
                    <div className="dw-vm-metrics">{t('Disk')} {formatBytes(u.disk_used_bytes || 0)} / {formatBytes(diskLimit)} · ↓{formatBytes(u.net_in_bytes || 0)} ↑{formatBytes(u.net_out_bytes || 0)}</div>
                </div>
            );
        }) : <NoData />;
    }
    return (
        <div>
            <Provisioning items={pending} render={(p) => p.label || p.image || t('Dedicated Containers')} />
            {list.map((c) => (
                <Row key={c.id} to={`/containers/${c.id}`} left={<>{c.name} <Text type="secondary" className="dw-mono">{specText(c)}</Text></>}
                    right={<ContainerStatusTag status={c.status} busy={c.busy} />} />
            ))}
            {!list.length && !pending.length ? <NoData /> : null}
        </div>
    );
};

const Vpc = ({ data, view }) => {
    const { t } = useTranslation();
    const list = data.list || [];
    return (
        <div>
            <div className="dw-kpis dw-kpis-2">
                <Kpi label="VPC" value={data.vpcs} />
                <Kpi label={t('Elastic IPs')} value={data.eips} sub={data.eips ? t('{{count}} bound', { count: data.eip_bound }) : null} />
            </div>
            <div className="dw-sub-title">{view === 'eip' ? t('Elastic IPs') : 'VPC'}</div>
            {list.length ? list.map((r) => (view === 'eip'
                ? <Row key={r.id} to={r.link} left={<span className="dw-mono">{r.ip}</span>} right={r.bound ? <Text type="secondary">{r.bound}</Text> : <StatusTag status={r.status} />} />
                : <Row key={r.id} to="/evm/vpc" left={<>{r.name} <Text type="secondary" className="dw-mono">{r.cidr}</Text></>} right={<Text type="secondary">{r.region || ''}</Text>} />
            )) : <NoData />}
        </div>
    );
};

const Domain = ({ data, view }) => {
    const { t } = useTranslation();
    const list = data.list || [];
    return (
        <div>
            <div className="dw-kpis dw-kpis-3">
                <Kpi label={t('Domains')} value={data.total} />
                <Kpi label={t('Expires within 30 days')} value={data.expiring} tone={data.expiring ? 'warn' : null} />
                <Kpi label={t('Auto-renewal')} value={data.auto_renew} tone={view === 'autorenew' && data.auto_off ? 'warn' : null} sub={view === 'autorenew' && data.auto_off ? t('{{count}} off', { count: data.auto_off }) : null} />
            </div>
            <div className="dw-sub-title">{view === 'autorenew' ? t('Auto-renewal disabled') : view === 'expiring' ? t('Expires within 30 days') : t('Expiring soon')}</div>
            {list.length ? list.map((r) => (
                <Row
                    key={r.id}
                    to={r.link}
                    left={r.name}
                    right={view === 'list' && r.status !== 'active' ? <StatusTag status={r.status} /> : (
                        <><Days d={r.days_left} />{view !== 'autorenew' && r.auto_renew ? <Text type="secondary">{t('· Auto')}</Text> : null}</>
                    )}
                />
            )) : <NoData text={view === 'autorenew' ? t('Auto-renew is on for all') : t('No domains expiring soon')} />}
        </div>
    );
};

const SSL_SOURCE = { order: '商业', cdn: 'CDN', hosting: '主机' };
const SSL_STATE = { issuing: ['签发中', 'processing'], failed: ['失败', 'error'], expired: ['已过期', 'error'], configure: ['待配置', 'warning'], validate: ['待验证', 'warning'] };

const Ssl = ({ data, view }) => {
    const { t } = useTranslation();
    const list = data.list || [];
    const exp = (data.expiring || 0) + (data.edge_expiring || 0);
    const sc = data.status_counts || {};
    const src = (r) => <Tag className="dw-mini-tag">{t(SSL_SOURCE[r.source] || r.source)}</Tag>;
    return (
        <div>
            {view === 'status' ? (
                <div className="dw-kpis dw-kpis-3">
                    <Kpi label={t('Normal')} value={sc.ok || 0} />
                    <Kpi label={t('Issuing')} value={sc.issuing || 0} tone={sc.issuing ? 'warn' : null} />
                    <Kpi label={t('Failed')} value={(sc.failed || 0) + (sc.expired || 0)} tone={sc.failed || sc.expired ? 'danger' : null} sub={sc.expired ? `${t('Expired')} ${sc.expired}` : null} />
                </div>
            ) : (
                <div className="dw-kpis dw-kpis-3">
                    <Kpi label={t('Certificate')} value={data.certs ?? data.total} tip={t('Includes certificates issued automatically for CDN and hosting')} />
                    <L to="/ssl/certificates" className="dw-kpi-link"><Kpi label={t('Pending')} value={data.pending} tone={data.pending ? 'warn' : null} /></L>
                    <Kpi label={t('Expires within 30 days')} value={view === 'expiring' ? data.expiring_all ?? exp : exp} tone={exp ? 'warn' : null} />
                </div>
            )}
            <div className="dw-sub-title">{view === 'status' ? t('Issuance / renewal') : view === 'expiring' ? t('Expires within 30 days') : t('Expiring soon')}</div>
            {list.length ? list.map((r) => (
                <Row
                    key={r.key}
                    to={r.link}
                    left={<>{src(r)} {r.name}</>}
                    right={view === 'status'
                        ? (() => { const s = SSL_STATE[r.state] || SSL_STATE.issuing; return <Tooltip title={r.error || null}><Tag color={s[1]} style={{ marginInlineEnd: 0 }}>{t(s[0])}</Tag></Tooltip>; })()
                        : r.status === 'expired' ? <StatusTag status="expired" /> : <Days d={r.days_left} />}
                />
            )) : <NoData text={view === 'status' ? t('Nothing being issued') : view === 'expiring' ? t('No certificates expiring soon') : t('No certificates')} />}
        </div>
    );
};

const Mail = ({ data, view }) => {
    const { t } = useTranslation();
    if (view === 'domains') {
        return (data.list || []).length ? data.list.map((d) => (
            <Row
                key={d.id}
                to="/webmail/domains"
                left={<>{d.name} {d.dns_verified ? null : <Tag color="warning" className="dw-mini-tag">{t('DNS validation pending')}</Tag>}</>}
                right={<Text type="secondary">{d.max_users > 0 ? `${d.users}/${d.max_users}` : d.users} · {formatBytes(d.used_bytes)}</Text>}
            />
        )) : <NoData />;
    }
    return (
        <div>
            <div className="dw-kpis dw-kpis-3">
                <Kpi label={t('Email domains')} value={data.total} sub={data.suspended ? t('{{count}} paused', { count: data.suspended }) : null} />
                <Kpi label={t('Email account')} value={data.users} />
                <Kpi label={t('Used storage')} value={formatBytes(data.used_bytes)} sub={data.quota_bytes ? t('{{space}} total', { space: formatBytes(data.quota_bytes) }) : null} />
            </div>
            <div className="dw-sub-title">{t('DNS validation pending')}</div>
            {(data.dns_pending_list || []).length ? data.dns_pending_list.map((d) => (
                <Row key={d.id} to="/webmail/domains" left={d.name} right={<span className="dw-warn-text">{t('Verify now')}</span>} />
            )) : <NoData text={t('All verified')} />}
        </div>
    );
};

const Tm = ({ data, view }) => {
    const { t } = useTranslation();
    if (view === 'streams') {
        return (data.list || []).length ? data.list.map((s) => (
            <Row
                key={s.id}
                to={`/tm/streams/${s.id}`}
                left={<>{s.name}{s.sandbox ? <Tag color="warning" className="dw-mini-tag">{t('Sandbox')}</Tag> : null}</>}
                right={<Text type="secondary">{s.monthly_limit > 0 ? `${fmtNum(s.monthly_used)} / ${fmtNum(s.monthly_limit)}` : fmtNum(s.monthly_used)} · {t('this month')}</Text>}
            />
        )) : <NoData />;
    }
    const tot = data.totals || {};
    const r = data.rates || {};
    return (
        <div>
            <div className="dw-kpis dw-kpis-3">
                <Kpi label={t('Sent (7 days)')} value={fmtNum(tot.total)} />
                <Kpi label={t('Delivery rate')} value={tot.total ? fmtPct(r.delivered) : '—'} />
                <Kpi label={t('Bounce rate')} value={tot.total ? fmtPct(r.bounced) : '—'} tone={r.bounced > 0.05 ? 'warn' : null} />
            </div>
            {(data.series || []).some((p) => p.count)
                ? <MiniTrend points={data.series.map((p) => ({ t: p.t, v: p.count }))} height={90} timeFormat="MM-DD" name={t('Send')} format={(v) => fmtNum(v)} />
                : <NoData text={t('Nothing sent in the last 7 days')} />}
        </div>
    );
};

const Backup = ({ data, view }) => {
    const { t } = useTranslation();
    const list = data.list || [];
    return (
        <div>
            <div className="dw-kpis dw-kpis-3">
                <Kpi label={t('Resources')} value={data.resources} sub={data.scheduled ? t('{{count}} scheduled', { count: data.scheduled }) : null} />
                <Kpi label={t('Backup points')} value={data.points} />
                <Kpi label={t('Failed')} value={data.failing} tone={data.failing ? 'danger' : null} />
            </div>
            <div className="dw-sub-title">{view === 'recent' ? t('Recent backups') : t('Resources')}</div>
            {list.length ? list.map((r) => (view === 'recent'
                ? <Row key={r.key} to={r.link} left={<>{r.name} <Text type="secondary">{r.kind === 'manual' ? t('Manual') : r.bytes ? formatBytes(r.bytes) : ''}</Text></>} right={<Text type="secondary" className="dw-mono">{formatDateTime(r.at)}</Text>} />
                : <Row key={`${r.type}${r.id}`} to={r.link} left={<>{r.name} {r.scheduled ? <Tag className="dw-mini-tag">{t('Scheduled')}</Tag> : null}</>}
                    right={r.fail_count ? <Tag color="error" style={{ marginInlineEnd: 0 }}>{t('Failed')} ×{r.fail_count}</Tag> : <Text type="secondary" className="dw-mono">{r.last_success_at ? formatDate(r.last_success_at) : '-'}</Text>} />
            )) : <NoData />}
        </div>
    );
};

const Activity = ({ data }) => {
    const { t } = useTranslation();
    const [tab, setTab] = useState('invoices');
    return (
        <div>
            <Segmented size="small" value={tab} onChange={setTab} options={[{ label: t('Invoices'), value: 'invoices' }, { label: t('Activity'), value: 'logs' }]} style={{ marginBottom: 8 }} />
            {tab === 'invoices' ? <InvoiceRows rows={data.invoices || []} /> : ((data.logs || []).length ? data.logs.map((r) => (
                <Row key={r.id} left={t(r.description || r.action)} right={<Text type="secondary">{formatDateTime(r.at)}</Text>} />
            )) : <NoData text={t('No activity yet')} />)}
        </div>
    );
};

const QUICK = [
    { label: '添加 CDN 域名', to: '/cdn/domains/new', icon: <CloudServerOutlined /> },
    { label: 'DNS 区域', to: '/dns/zones', icon: <ApartmentOutlined /> },
    { label: '注册域名', to: '/domain-registration/search', icon: <GlobalOutlined /> },
    { label: '购买证书', to: '/ssl/browse', icon: <SafetyCertificateOutlined /> },
    { label: '云服务器', to: '/evm/vms', icon: <ClusterOutlined /> },
    { label: '充值', to: '/billing/balance', icon: <WalletOutlined /> },
    { label: '账单', to: '/billing/invoices', icon: <FileTextOutlined /> },
    { label: '购物车', to: '/billing/cart', icon: <ShoppingCartOutlined /> },
];

const Quick = () => {
    const { t } = useTranslation();
    return (
        <div className="dw-quick">
            {QUICK.map((q) => (
                <L key={q.to} to={q.to} className="dw-quick-item"><span className="dw-quick-icon">{q.icon}</span>{t(q.label)}</L>
            ))}
        </div>
    );
};

// ---------------------------------------------------------------- onboarding + product showcase

/** Getting-started checklist: common steps + the focus product's steps. State from Dashboard (ctx.onboarding), steps detected server-side. */
const ONB_STEPS = {
    email: { label: 'Verify your email', action: 'Resend email' },
    secure: { label: 'Secure your account', tip: 'Turn on 2FA or bind Telegram', action: 'Set up', to: () => '/settings?tab=security' },
    cdn_domain: { label: 'Add a site to CDN', action: 'Add domain', to: () => '/cdn/domains/new' },
    cdn_dns: { label: 'Point DNS to the CDN', tip: 'Add the CNAME shown on the site page at your DNS provider', action: 'View CNAME', to: (d) => (d ? `/cdn/domains/${encodeURIComponent(d)}` : '/cdn/domains'), needsDomain: true },
    cdn_https: { label: 'Turn on HTTPS', action: 'Enable', to: (d) => (d ? `/cdn/domains/${encodeURIComponent(d)}/ssl` : '/cdn/domains'), needsDomain: true },
    trial: { label: 'Try a paid CDN plan', action: 'Try free', to: () => '/cdn/plans', optional: true },
    llm_open: { label: 'Open your LLM account', action: 'Open', to: () => '/llm/account' },
    llm_fund: { label: 'Add credit', tip: 'Use your coupon or transfer from your balance', action: 'Add credit', to: () => '/llm/account' },
    llm_call: { label: 'Make your first API call', tip: 'Create an API key and call the OpenAI-compatible endpoint', action: 'Get API key', to: () => '/llm/tokens' },
    evm_key: { label: 'Add an SSH key', action: 'Add key', to: () => '/evm/ssh-keys' },
    evm_vm: { label: 'Launch a server', action: 'Launch', to: () => '/evm/vms/purchase' },
    s3_bucket: { label: 'Create a bucket', action: 'Create', to: () => '/s3' },
    s3_key: { label: 'Create an access key', action: 'Create', to: () => '/s3/keys' },
    s3_upload: { label: 'Upload your first file', tip: 'Use the bucket page or any S3 client', action: 'Open', to: () => '/s3' },
    dns_zone: { label: 'Add your domain to DNS', action: 'Add domain', to: () => '/dns/zones?new=1' },
    dns_ns: { label: 'Point nameservers to us', tip: 'At your registrar, set the nameservers shown on the zone page', action: 'View', to: () => '/dns/zones' },
    domain_reg: { label: 'Register a domain', action: 'Search', to: () => '/domain-registration/search' },
};

const Onboarding = ({ ctx }) => {
    const { t } = useTranslation();
    const o = ctx?.onboarding;
    const [sending, setSending] = useState(false);
    if (!o || !Array.isArray(o.steps)) return null;
    const focus = onbProduct(o.focus);
    const steps = [...o.steps, ...(ctx.trial && focus.key === 'cdn' ? [{ key: 'trial', done: false }] : [])].filter((s) => ONB_STEPS[s.key]);
    const next = steps.find((s) => !s.done && !ONB_STEPS[s.key].optional);
    const resend = async () => {
        setSending(true);
        try {
            await ctx.resendEmail?.();
        } finally {
            setSending(false);
        }
    };
    return (
        <div className="dw-onb">
            <div className="dw-onb-head">
                <Dropdown
                    trigger={['click']}
                    menu={{
                        items: ONB_PRODUCTS.map((p) => ({ key: p.key, icon: p.icon, label: t(p.name) })),
                        selectable: true,
                        selectedKeys: [focus.key],
                        onClick: ({ key }) => ctx.setFocus?.(key),
                    }}
                >
                    <Button size="small" type="text" className="dw-onb-focus">{focus.icon} {t(focus.name)} <DownOutlined /></Button>
                </Dropdown>
                <Progress percent={o.total ? Math.round((o.done / o.total) * 100) : 0} size="small" format={() => `${o.done}/${o.total}`} />
            </div>
            <div className="dw-onb-steps">
                {steps.map((s, i) => {
                    const def = ONB_STEPS[s.key];
                    const blocked = def.needsDomain && !o.domain;
                    const primary = next && next.key === s.key;
                    let btn = null;
                    if (!s.done) {
                        btn = s.key === 'email'
                            ? <Button size="small" type={primary ? 'primary' : 'default'} loading={sending} onClick={resend}>{t(def.action)}</Button>
                            : (
                                <L to={def.to(o.domain)} onClick={blocked ? (e) => e.preventDefault() : undefined}>
                                    <Button size="small" type={primary ? 'primary' : 'default'} disabled={blocked}>{t(def.action)}</Button>
                                </L>
                            );
                    }
                    return (
                        <div key={s.key} className={`dw-onb-step${s.done ? ' is-done' : ''}${primary ? ' is-next' : ''}`}>
                            <span className="dw-onb-mark">{s.done ? <CheckCircleFilled /> : (def.optional ? <PlusOutlined /> : i + 1)}</span>
                            <span className="dw-onb-label">
                                {t(def.label)}
                                {def.optional ? <Tag className="dw-onb-tag">{t('Optional')}</Tag> : null}
                                {def.tip && !s.done ? <Tooltip title={t(def.tip)}><InfoCircleFilled className="dw-onb-tip" /></Tooltip> : null}
                            </span>
                            {btn}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

/** Intro state for a product card the user has none of: one line + one CTA (no bare "None yet"). */
const Showcase = ({ icon, text, cta, to, extra }) => {
    const { t } = useTranslation();
    return (
        <div className="dw-show">
            <span className="dw-show-icon">{icon}</span>
            <div className="dw-show-body">
                <Text type="secondary">{t(text)}</Text>
                {extra}
            </div>
            <L to={to}><Button type="primary" size="small" icon={<PlusOutlined />}>{t(cta)}</Button></L>
        </div>
    );
};

const CdnShowcase = ({ ctx }) => {
    const { t } = useTranslation();
    return (
        <Showcase
            icon={<CloudServerOutlined />}
            text="Faster, protected sites on our global edge"
            cta="Add domain"
            to="/cdn/domains/new"
            extra={ctx?.trial ? <L to="/cdn/plans" className="dw-show-hint">{t('Free trial available')}</L> : null}
        />
    );
};

const DnsShowcase = () => <Showcase icon={<ApartmentOutlined />} text="Fast authoritative DNS for your domains" cta="Add zone" to="/dns/zones?new=1" />;

const EvmShowcase = ({ data, size }) => {
    const { t } = useTranslation();
    const avail = (data.regions || []).filter((r) => r.available);
    return (
        <div className={`dw-evm${size === 'full' ? ' is-wide' : ''}`}>
            <div className="dw-show is-col">
                <span className="dw-show-icon"><DesktopOutlined /></span>
                <Text type="secondary">{t('Cloud servers in {{count}} regions', { count: avail.length })}</Text>
                <div className="dw-region-chips">
                    {avail.map((r) => <span key={r.area_id} className="dw-region" title={(r.zones || []).join('、')}><CountryFlag code={r.code} style={{ marginInlineEnd: 4 }} />{regionName(r)}</span>)}
                </div>
                <L to="/evm/vms/purchase"><Button type="primary" size="small" icon={<PlusOutlined />}>{t('Create instance')}</Button></L>
            </div>
            <div className="dw-evm-map">
                <RegionMap regions={data.regions || []} height={size === 'full' ? 240 : 200} />
                <div className="dw-map-legend"><span><span className="dw-sw is-avail" />{t('Available for activation')}</span></div>
            </div>
        </div>
    );
};

/**
 * Widget registry.
 *  fetch: server key (null = static)   group: request batch (slow sources get their own)
 *  product: product card — empty(data) shows an in-card 开通 entry (Showcase: intro state + CTA instead)
 */
export const WIDGETS = {
    onboarding: { Body: Onboarding, fetch: null },
    attention: { Body: Attention, fetch: 'attention', group: 'core' },
    account: { Body: Account, fetch: 'account', group: 'core', link: '/billing/invoices' },
    services: { Body: Services, fetch: 'services', group: 'core', link: '/billing/subscription' },
    cdn: { Body: Cdn, fetch: 'cdn', group: 'cdn', link: '/cdn/analytics', product: 'cdn', empty: (d) => !!d.empty, Showcase: CdnShowcase },
    dns: { Body: Dns, fetch: 'dns', group: 'dns', link: '/dns/analytics', product: 'dns', empty: (d) => !!d.empty, Showcase: DnsShowcase },
    evm: { Body: Evm, fetch: 'evm', group: 'evm', link: '/evm/vms', product: 'evm', empty: (d) => !d.total && !arr(d.pending).length, Showcase: EvmShowcase },
    host_shared: { Body: HostShared, fetch: 'host_shared', group: 'host', link: '/host_shared/vhosts', product: 'host_shared', empty: (d) => !!d.empty },
    container: { Body: Container, fetch: 'container', group: 'host', link: '/containers', product: 'container', empty: (d) => !!d.empty },
    vpc: { Body: Vpc, fetch: 'vpc', group: 'core', link: '/evm/vpc', product: 'vpc', empty: (d) => !!d.empty },
    domain: { Body: Domain, fetch: 'domain', group: 'core', link: '/domain-registration/my-domains', product: 'domain', empty: (d) => !d.total },
    ssl: { Body: Ssl, fetch: 'ssl', group: 'core', link: '/ssl/certificates', product: 'ssl', empty: (d) => !!d.empty },
    mail: { Body: Mail, fetch: 'mail', group: 'core', link: '/webmail/domains', product: 'mail', empty: (d) => !d.total },
    tm: { Body: Tm, fetch: 'tm', group: 'tm', link: '/tm/streams', product: 'tm', empty: (d) => !!d.empty },
    backup: { Body: Backup, fetch: 'backup', group: 'core', link: null, product: 'backup', empty: (d) => !!d.empty },
    activity: { Body: Activity, fetch: 'activity', group: 'core', link: '/billing/invoices' },
    quick: { Body: Quick, fetch: null },
};
